// #JOBPNL_PDF_V1 (2026-11-03) — Bill: "P&L need a PDF Version." Owner Dash → job drill (Job P&L · Daily Report).
// Drawn as real PDF text/table with jsPDF (not a screenshot) so 100+ daily rows break cleanly across pages with the
// header repeated, stay sharp on paper, and are searchable. Opens in the DOPE print viewer (#PRINT_VIEW_V1).
// App.js passes the SAME numbers the overlay already computed — nothing is re-derived here.
import { openPrintView } from './printView';

const money = n => (n < 0 ? '-' : '') + '$' + Math.abs(Math.round(Number(n) || 0)).toLocaleString();
const md = (s) => { const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : String(s || ''); };
const hex = (h, fb = [120, 120, 120]) => { const m = String(h || '').match(/^#?([0-9a-f]{6})$/i); if (!m) return fb; const n = parseInt(m[1], 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const logoData = async () => { try { const b = await (await fetch('/duc-logo.png')).blob(); return await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.onerror = () => r(null); fr.readAsDataURL(b); }); } catch { return null; } };
const imgSize = (src) => new Promise(r => { const i = new Image(); i.onload = () => r({ w: i.naturalWidth, h: i.naturalHeight }); i.onerror = () => r(null); i.src = src; });

// d = { duc, cust, sub, tiles:[{l,v,c}], contractNote, buckets:[{k,v,c}], bktBase, bktStale, staleNote, entries:[{date,sup,rig,bore,stage,soil,lf,comment,flag,isPull}], rainCt, delayCt }
export async function jobPnlPdf(d) {
  const pv = openPrintView({ title: `${d.duc} · Job P&L`, status: 'Building the Job P&L PDF…' });   // show right away (from the click)
  try {
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
    const PW = pdf.internal.pageSize.getWidth(), PH = pdf.internal.pageSize.getHeight(), M = 32, W = PW - M * 2;
    const INK = [17, 17, 17], MUTED = [110, 110, 110], LINE = [215, 215, 215], GOLD = [176, 138, 20];
    const logo = await logoData(); const ls = logo ? await imgSize(logo) : null;
    let y = M;

    // ── header ──
    if (logo && ls) { const h = 34, w = ls.w * h / ls.h; pdf.addImage(logo, 'PNG', M, y, w, h); }
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5); pdf.setTextColor(...GOLD);
    pdf.text('JOB P&L · DAILY REPORT', PW - M, y + 10, { align: 'right' });
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...MUTED);
    pdf.text(new Date().toLocaleDateString(), PW - M, y + 22, { align: 'right' });
    y += logo && ls ? 62 : 30;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18); pdf.setTextColor(...INK);
    pdf.text(`${d.duc} · ${d.cust || '—'}`, M, y); y += 15;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5); pdf.setTextColor(...MUTED);
    pdf.text(d.sub || '', M, y); y += 10;
    pdf.setDrawColor(...INK); pdf.setLineWidth(1.2); pdf.line(M, y, PW - M, y); y += 16;

    // ── P&L tiles ──
    const sect = (t) => { pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(...MUTED); pdf.text(t.toUpperCase(), M, y); y += 9; };
    if (d.tiles && d.tiles.length) {
      sect('Job P&L');
      const n = d.tiles.length, gap = 6, tw = (W - gap * (n - 1)) / n, th = 40;
      d.tiles.forEach((t, i) => {
        const x = M + i * (tw + gap);
        pdf.setDrawColor(...LINE); pdf.setLineWidth(0.6); pdf.roundedRect(x, y, tw, th, 3, 3, 'S');
        pdf.setFillColor(...hex(t.c)); pdf.rect(x, y, tw, 2.5, 'F');
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(6.8); pdf.setTextColor(...MUTED); pdf.text(String(t.l).toUpperCase(), x + 6, y + 13);
        pdf.setFontSize(t.v.length > 10 ? 10.5 : 12.5); pdf.setTextColor(...hex(t.pc || t.c, INK)); pdf.text(String(t.v), x + 6, y + 31);
      });
      y += th + 10;
    } else { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(...MUTED); pdf.text('Nothing invoiced or costed for this job yet.', M, y + 4); y += 18; }

    if (d.contractNote) {
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(...INK);
      const ln = pdf.splitTextToSize(d.contractNote, W - 16);
      pdf.setFillColor(252, 245, 222); pdf.setDrawColor(230, 200, 110); pdf.roundedRect(M, y, W, ln.length * 11 + 10, 3, 3, 'FD');
      pdf.text(ln, M + 8, y + 13); y += ln.length * 11 + 18;
    }

    // ── cost split bar + legend ──
    if (d.buckets && d.buckets.length) {
      if (d.bktStale && d.staleNote) { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(...GOLD); const ln = pdf.splitTextToSize('! ' + d.staleNote, W); pdf.text(ln, M, y); y += ln.length * 10 + 2; }
      const base = Math.max(1, d.bktBase); let x = M;
      d.buckets.forEach(b => { const w = W * b.v / base; pdf.setFillColor(...hex(b.c)); pdf.rect(x, y, w, 8, 'F'); x += w; });
      y += 18;
      const cols = 3, cw = W / cols;
      d.buckets.forEach((b, i) => {
        const cx = M + (i % cols) * cw, cy = y + Math.floor(i / cols) * 13;
        pdf.setFillColor(...hex(b.c)); pdf.rect(cx, cy - 6.5, 7, 7, 'F');
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(...INK); pdf.text(b.k, cx + 11, cy);
        pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...MUTED); pdf.text(`${money(b.v)} · ${Math.round(b.v / base * 100)}%`, cx + cw - 14, cy, { align: 'right' });
      });
      y += Math.ceil(d.buckets.length / cols) * 13 + 8;
    }

    // ── BY BORE (#JOBPNL_BYBORE_V1) — same rules as the DailyOps PRODUCTION table: DAYS = every day on the bore,
    // PULLED LF = Pull-stage footage, FIN/LF = pulled ÷ days. Plus start → pull dates and where the days went.
    const ents = d.entries || [];
    {
      const bm = {};
      ents.forEach(e => {
        const bore = String(e.bore || '').trim(); if (!bore || !e.date) return;
        const b = bm[bore] || (bm[bore] = { dates: new Set(), sups: {}, pulled: 0, pullDate: '', st: { mob: new Set(), pilot: new Set(), ream: new Set(), swab: new Set(), pull: new Set(), wx: new Set(), dly: new Set() } });
        b.dates.add(e.date);
        const sup = String(e.sup || '').trim(); if (sup) (b.sups[sup] = b.sups[sup] || new Set()).add(e.date);
        const sg = String(e.stage || '').toLowerCase();
        if (e.flag === 'rain') b.st.wx.add(e.date); else if (e.flag === 'delay' || sg === 'delay') b.st.dly.add(e.date);
        else if (sg === 'pull') b.st.pull.add(e.date);
        else if (/pilot/.test(sg)) b.st.pilot.add(e.date);
        else if (/ream|^r\d/.test(sg)) b.st.ream.add(e.date);
        else if (/swab|clean/.test(sg)) b.st.swab.add(e.date);
        else if (/mob|setup|set up/.test(sg)) b.st.mob.add(e.date);
        if (sg === 'pull') { b.pulled += Number(e.drilledLF) || 0; if (!b.pullDate || e.date > b.pullDate) b.pullDate = e.date; }
      });
      const nat = (a, b) => { const na = parseFloat(a), nb = parseFloat(b); return (!isNaN(na) && !isNaN(nb) && na !== nb) ? na - nb : String(a).localeCompare(String(b)); };
      const bores = Object.keys(bm).sort(nat);
      if (bores.length) {
        const r1 = n => Math.round(n * 10) / 10;
        const rows = bores.map(k => { const b = bm[k]; const ds = [...b.dates].sort(); const se = Object.entries(b.sups).sort((x, y) => y[1].size - x[1].size);
          const days = b.dates.size;
          return [k, se.length ? se.map(s => s[0]).slice(0, 2).join(' / ') + (se.length > 2 ? ` +${se.length - 2}` : '') : '—', md(ds[0]), b.pullDate ? md(b.pullDate) : 'open',
            String(days), String(b.st.mob.size || ''), String(b.st.pilot.size || ''), String(b.st.ream.size || ''), String(b.st.swab.size || ''), String(b.st.pull.size || ''),
            String((b.st.wx.size + b.st.dly.size) || ''), b.pulled ? Math.round(b.pulled).toLocaleString() : '', days && b.pulled ? String(r1(b.pulled / days)) : '—']; });
        const gDays = bores.reduce((t, k) => t + bm[k].dates.size, 0), gPull = Math.round(bores.reduce((t, k) => t + bm[k].pulled, 0));
        const sum = (f) => String(bores.reduce((t, k) => t + bm[k].st[f].size, 0) || '');
        const tot = ['TOTAL', `${bores.length} ${bores.length === 1 ? 'bore' : 'bores'}`, '', '', String(gDays), sum('mob'), sum('pilot'), sum('ream'), sum('swab'), sum('pull'),
          String(bores.reduce((t, k) => t + bm[k].st.wx.size + bm[k].st.dly.size, 0) || ''), gPull ? gPull.toLocaleString() : '', gDays && gPull ? String(r1(gPull / gDays)) : '—'];
        const BC = [['Bore', 38], ['Sup', 0], ['Start', 42], ['Pulled', 42], ['Days', 32, 'r'], ['Mob/Set', 40, 'r'], ['Pilot', 30, 'r'], ['Ream', 30, 'r'], ['Swab', 32, 'r'], ['Pull', 28, 'r'], ['Rain/Dly', 40, 'r'], ['Pulled LF', 48, 'r'], ['FIN/LF', 38, 'r']];
        BC[1][1] = W - BC.reduce((s, c) => s + c[1], 0);
        const RH = 14;
        const need = (rows.length + 2) * RH + 34;
        y += 4; if (y + Math.min(need, 120) > PH - M - 24) { pdf.addPage(); y = M; }
        sect('By Bore · time on each bore');
        const drawRow = (vals, kind) => {
          if (y + RH > PH - M - 24) { pdf.addPage(); y = M; }
          if (kind === 'h') { pdf.setFillColor(238, 238, 238); pdf.rect(M, y, W, RH, 'F'); }
          if (kind === 't') { pdf.setFillColor(252, 245, 222); pdf.rect(M, y, W, RH, 'F'); }
          pdf.setFont('helvetica', kind ? 'bold' : 'normal'); pdf.setFontSize(kind === 'h' ? 7 : 8.2); pdf.setTextColor(...(kind === 'h' ? MUTED : INK));
          let x = M; BC.forEach(([, w, a], i) => { const t = pdf.splitTextToSize(String(kind === 'h' ? vals[i].toUpperCase() : vals[i]), w - 8)[0] || '';
            pdf.text(t, a === 'r' ? x + w - 4 : x + 4, y + 9.8, a === 'r' ? { align: 'right' } : undefined); x += w; });
          y += RH; if (!kind) { pdf.setDrawColor(...LINE); pdf.setLineWidth(0.4); pdf.line(M, y, PW - M, y); }
        };
        drawRow(BC.map(c => c[0]), 'h'); rows.forEach(r => drawRow(r)); drawRow(tot, 't');
        y += 10; pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.setTextColor(...MUTED);
        const fn = pdf.splitTextToSize('DAYS = every day logged on the bore (calendar dates) · stage columns = days in that stage · PULLED LF = Pull-stage footage · FIN/LF = pulled ÷ days — same rules as the DailyOps report.', W);
        pdf.text(fn, M, y); y += fn.length * 8.5 + 10;
      }
    }

    // ── daily report table ──
    y += 4; sect(`Daily Report · ${ents.length} ${ents.length === 1 ? 'entry' : 'entries'}${(d.rainCt || d.delayCt) ? ` · ${d.rainCt} rain · ${d.delayCt} delay` : ''}`);
    const C = [['Date', 44], ['Sup', 64], ['Rig', 50], ['Bore', 28], ['Stage', 46], ['Soil', 34], ['LF', 38, 'r']];
    const fixed = C.reduce((s, c) => s + c[1], 0); C.push(['Comment', W - fixed]);
    const PAD = 4, LH = 9.6, FOOT = 24;
    const head = () => {
      pdf.setFillColor(238, 238, 238); pdf.rect(M, y, W, 15, 'F');
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.2); pdf.setTextColor(...MUTED);
      let x = M; C.forEach(([h, w, a]) => { pdf.text(h.toUpperCase(), a === 'r' ? x + w - PAD : x + PAD, y + 10, a === 'r' ? { align: 'right' } : undefined); x += w; });
      y += 15;
    };
    if (!ents.length) { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(...MUTED); pdf.text('No daily log entries for this job yet.', M, y + 6); }
    else {
      head();
      ents.forEach(e => {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
        const cm = pdf.splitTextToSize(String(e.comment || ''), C[7][1] - PAD * 2);
        const rh = Math.max(1, cm.length) * LH + 5;
        if (y + rh > PH - M - FOOT) { pdf.addPage(); y = M; head(); }
        const bg = e.flag === 'rain' ? [226, 235, 252] : e.flag === 'delay' ? [252, 243, 214] : e.isPull ? [221, 245, 230] : null;
        if (bg) { pdf.setFillColor(...bg); pdf.rect(M, y, W, rh, 'F'); }
        if (e.isPull) { pdf.setFillColor(22, 163, 74); pdf.rect(M, y, 2.2, rh, 'F'); }
        const stage = e.flag === 'rain' ? 'RAIN' : e.flag === 'delay' ? 'DELAY' : e.isPull ? 'PULL' : (e.stage || '—');
        const vals = [md(e.date), e.sup || '—', e.rig || '—', String(e.bore || '—'), stage, e.soil || '—', (Number(e.lf) || 0).toLocaleString()];
        let x = M;
        vals.forEach((v, i) => {
          const [, w, a] = C[i];
          const strong = i === 4 && (e.flag || e.isPull);
          pdf.setFont('helvetica', strong || i === 6 ? 'bold' : 'normal');
          pdf.setTextColor(...(strong ? (e.flag === 'rain' ? [37, 99, 235] : e.flag === 'delay' ? [161, 112, 0] : [22, 128, 61]) : INK));
          const t = pdf.splitTextToSize(String(v), w - PAD * 2)[0] || '';
          pdf.text(t, a === 'r' ? x + w - PAD : x + PAD, y + 9.5, a === 'r' ? { align: 'right' } : undefined);
          x += w;
        });
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...(e.flag ? INK : [70, 70, 70]));
        if (cm.length) pdf.text(cm, x + PAD, y + 9.5, { lineHeightFactor: LH / 8 });
        y += rh; pdf.setDrawColor(...LINE); pdf.setLineWidth(0.4); pdf.line(M, y, PW - M, y);
      });
    }

    // ── footer on every page ──
    const pages = pdf.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      pdf.setPage(p); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(...MUTED);
      pdf.text(`DOPE · Job P&L · ${d.duc}`, M, PH - M + 12);
      pdf.text(`Page ${p} of ${pages} · printed ${new Date().toLocaleString()}`, PW - M, PH - M + 12, { align: 'right' });
    }
    const fn = `DUC ${d.duc} ${d.cust || ''} Job P&L.pdf`.replace(/[\\/:*?"<>|#%]/g, '-').replace(/\s+/g, ' ');
    pv.setPdf(pdf.output('blob'), fn);
  } catch (err) { pv.fail(err); }
}
